<!--
  LjReport.vue

  Per-stock Sales Report (LJ) editor with two modes on the same page:

  1. EDIT VIEW (always visible) — CoreUI card-based grid for admins. Every
     derived cell shows a `↳ from Entity › Field` badge so admins can
     trace the data lineage. Computed totals (K / S / V / Z) render as
     prominent success badges with their formula on a second line.
     Editable cells use standard CInput controls with a primary-color
     left-border accent.

  2. PDF TEMPLATE (off-screen, captured by html2pdf) — a TABLE-grid layout
     pixel-matching references/BM2019.pdf: thin black borders, yellow
     highlights on the headline cells, monospaced right-aligned amounts,
     parentheses for negatives, DATE/APP/APP triplet columns. The off-
     screen positioning (`left: -10000px`) keeps it out of the visible UI
     but in the DOM so html2canvas can rasterize it.
-->
<template>
  <div class="lj-report-page">
    <!-- ===== Top action bar ===== -->
    <CCard class="action-bar mb-3 no-print">
      <CCardBody class="py-3">
        <div class="d-flex justify-content-between align-items-center flex-wrap">
          <div>
            <h4 class="mb-0">
              <i class="fas fa-file-alt me-2 text-primary"></i>
              <span>LJ Report</span>
              <span v-if="obj.stockNo" class="text-muted">— {{ obj.stockNo }}</span>
            </h4>
            <small v-if="loaded" class="text-muted">
              <span v-if="customerName">{{ customerName }}</span>
              <span v-if="salesmanName"> · {{ salesmanName }}</span>
              <span v-if="monthsLabel"> · {{ monthsLabel }}</span>
              <span v-if="loanBankName"> · {{ loanBankName }}</span>
            </small>
          </div>
          <div class="action-buttons">
            <CButton color="secondary" variant="outline" @click="goBack" class="me-2">
              <i class="fas fa-arrow-left me-1"></i> Back
            </CButton>
            <CButton color="primary" @click="save" :disabled="!loaded || saving" class="me-2">
              <CSpinner v-if="saving" size="sm" class="me-1" />
              <i v-else class="fas fa-save me-1"></i>
              {{ saving ? "Saving..." : "Save" }}
            </CButton>
            <CButton color="success" @click="downloadPdf" :disabled="!loaded || saving || generatingPdf">
              <CSpinner v-if="generatingPdf" size="sm" class="me-1" />
              <i v-else class="fas fa-file-pdf me-1"></i>
              {{ generatingPdf ? "Generating..." : "Download PDF" }}
            </CButton>
          </div>
        </div>
      </CCardBody>
    </CCard>

    <!-- ===== Loading state ===== -->
    <div v-if="!loaded" class="text-center py-5">
      <CSpinner color="primary" />
      <p class="text-muted mt-3">Loading LJ Report...</p>
    </div>

    <!-- ===== Edit view ===== -->
    <div v-else class="edit-view">
      <CRow>
        <!-- LEFT COLUMN -->
        <CCol lg="6">

          <!-- Customer & Sale Info -->
          <CCard class="section-card">
            <CCardHeader class="section-header">
              <i class="fas fa-user-tie me-2"></i>
              <strong>Customer &amp; Sale Info</strong>
            </CCardHeader>
            <CCardBody>
              <DerivedField label="S/Representative" :value="salesmanName" source="Sale › Salesman" />
              <DerivedField label="Stock No" :value="obj.stockNo" source="Stock › Stock No" />
              <DerivedField label="Customer Name" :value="customerName" source="Sale › Customer › Name" />
              <DerivedField label="Address" :value="customerAddress" :multiline="true" source="Sale › Customer › Address" />
              <DerivedField label="I/C Numbers" :value="customerIc" source="Sale › Customer › IC Number" />
              <DerivedField label="Date of Birth" :value="customerDobDisplay" source="Customer › Date of Birth" />
              <DerivedField label="Phone (Home)" :value="customerPhoneHome" source="Customer › Phone (Home)" />
              <DerivedField label="Phone (Office)" :value="customerPhoneOffice" source="Customer › Phone (Office)" />
              <hr class="my-2" />
              <DerivedField label="Months" :value="monthsLabel" source="Registration › Vehicle Registration Date" />
              <DerivedField label="Finance" :value="loanBankName" source="Sale › Loan › Bank Name" />
              <DerivedField label="Reg. Date" :value="regDateLabel" source="Registration › Vehicle Registration Date" />
              <DerivedField label="Reg. Numbers" :value="regNumber" source="Registration › Vehicle Registration No" />
            </CCardBody>
          </CCard>

          <!-- Vehicle -->
          <CCard class="section-card">
            <CCardHeader class="section-header">
              <i class="fas fa-car me-2"></i>
              <strong>Vehicle</strong>
            </CCardHeader>
            <CCardBody>
              <DerivedField label="Model / Colour" :value="modelColor" source="Stock › Vehicle (Brand + Model + Colour)" />
              <DerivedField label="Chassis No." :value="chassisNo" source="Stock › Vehicle › Chassis No" />
              <DerivedField label="Engine No." :value="engineNo" source="Stock › Vehicle › Engine No" />
            </CCardBody>
          </CCard>

          <!-- Cost Breakdown -->
          <CCard class="section-card">
            <CCardHeader class="section-header">
              <i class="fas fa-receipt me-2"></i>
              <strong>Cost Breakdown</strong>
            </CCardHeader>
            <CCardBody>
              <DerivedField label="Body Price" :value="formatMoneyDisplay(bodyPrice)" source="Administrative Cost › Body Price" />
              <DerivedField label="Insurance" :value="formatMoneyDisplay(insurance)" source="Administrative Cost › Insurance Amount" />
              <DerivedField label="Insurance Company" :value="insuranceCompanyName" source="Administrative Cost › Insurance Company Name" />
              <DerivedField label="Road Tax" :value="formatMoneyDisplay(roadtax)" source="Administrative Cost › Roadtax Amount" />
              <DerivedField label="Processing Fee / Warranty" :value="warranty" source="Sale › Warranty" />
              <DerivedField label="Warranty Type" :value="warrantyType" source="Sale › Warranty Type" />
              <hr class="my-2" />
              <EditableNumberField label="DEP. A" v-model="form.depA" />
              <EditableNumberField label="DEP. B" v-model="form.depB" />
              <EditableNumberField label="TT" v-model="form.tt" />
              <EditableNumberField label="DUTY" v-model="form.duty" />
              <EditableTextField label="Interchange No." v-model="form.interchangeNo" />

              <!-- Miscellaneous Cost rows -->
              <div class="line-items mt-3">
                <label class="line-items-label">
                  Miscellaneous Cost
                  <small class="text-muted">(line items)</small>
                </label>
                <div v-for="(m, i) in form.miscCosts" :key="'misc-' + i" class="line-item-row">
                  <CInput
                    v-model="m.name"
                    placeholder="Item name (e.g. INTERCHANGE)"
                    class="mb-0 flex-grow-1"
                  />
                  <CInput
                    v-model.number="m.amount"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    prepend="RM"
                    class="mb-0 amount-input"
                  />
                  <CButton size="sm" color="danger" variant="outline" @click="removeMisc(i)" title="Remove this line">
                    <i class="fas fa-trash me-1"></i> Delete
                  </CButton>
                </div>
                <CButton size="sm" color="secondary" variant="outline" @click="addMisc" class="mt-1">
                  <i class="fas fa-plus me-1"></i> Add Cost Line
                </CButton>
              </div>

              <hr class="my-3" />
              <ComputedTotal
                label="TOTAL COST PRICE = K"
                :value="totalCostPrice"
                formula="Body + Insurance + Roadtax + TT + Duty + Misc Costs + Processing Fee / Warranty"
              />
            </CCardBody>
          </CCard>

          <!-- Title & Settlement -->
          <CCard class="section-card">
            <CCardHeader class="section-header">
              <i class="fas fa-flag-checkered me-2"></i>
              <strong>Title &amp; Settlement</strong>
            </CCardHeader>
            <CCardBody>
              <EditableTextField label="Title" v-model="form.title" />
              <EditableTextField label="NO FP/FS" v-model="form.noFpFs" />
              <EditableDateField label="Date In" v-model="form.dateIn" />
              <EditableTextField label="Floor Plan" v-model="form.floorPlan" />
              <EditableTextField label="Floor Stocking" v-model="form.floorStocking" />
              <hr class="my-2" />
              <EditableDateField label="Settlement Date" v-model="form.settlementDate" />
              <EditableNumberField label="AMT SETTLE" v-model="form.amtSettle" />
              <EditableNumberField label="AMT SOLA" v-model="form.amtSola" />
              <EditableNumberField label="INT FS" v-model="form.intFs" />
            </CCardBody>
          </CCard>
        </CCol>

        <!-- RIGHT COLUMN -->
        <CCol lg="6">

          <!-- Pricing OTR -->
          <CCard class="section-card">
            <CCardHeader class="section-header">
              <i class="fas fa-tags me-2"></i>
              <strong>Pricing OTR</strong>
            </CCardHeader>
            <CCardBody>
              <DerivedField label="Price (O.T.R) = K" :value="formatMoneyDisplay(totalCostPrice)" source="Computed: K" />
              <EditableNumberField label="INS-10% LESS" v-model="form.ins10PercentLess" />

              <!-- Discount line items -->
              <div class="line-items mt-3">
                <label class="line-items-label">
                  Discount
                  <small class="text-muted">(line items)</small>
                </label>
                <div v-for="(d, i) in form.discounts" :key="'disc-' + i" class="line-item-row">
                  <CInput
                    v-model="d.name"
                    placeholder="Discount item"
                    class="mb-0 flex-grow-1"
                  />
                  <CInput
                    v-model.number="d.amount"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    prepend="RM"
                    class="mb-0 amount-input"
                  />
                  <CButton size="sm" color="danger" variant="outline" @click="removeDiscount(i)" title="Remove this line">
                    <i class="fas fa-trash me-1"></i> Delete
                  </CButton>
                </div>
                <CButton size="sm" color="secondary" variant="outline" @click="addDiscount" class="mt-1">
                  <i class="fas fa-plus me-1"></i> Add Discount Line
                </CButton>
              </div>

              <EditableNumberField label="Withdraw NCD" v-model="form.withdrawNcd" class="mt-3" />
              <hr class="my-3" />
              <ComputedTotal
                label="TOTAL PRICE (O.T.R) = S"
                :value="totalPriceOtr"
                formula="K − INS-10% − Discounts − Withdraw NCD"
              />
            </CCardBody>
          </CCard>

          <!-- Loan & Deposit -->
          <CCard class="section-card">
            <CCardHeader class="section-header">
              <i class="fas fa-piggy-bank me-2"></i>
              <strong>Loan &amp; Deposit</strong>
            </CCardHeader>
            <CCardBody>
              <DerivedField
                label="Loan"
                :value="loanLabel"
                source="Sale › Loan › Approved Loan Amount + Bank Name"
              />

              <div class="deposit-breakdown mt-3">
                <label class="line-items-label">
                  Deposit Breakdown
                  <small class="text-muted">
                    <i class="fas fa-info-circle me-1"></i>
                    Read-only — managed on the Stock › Sale tab (Customer Payments bucket)
                  </small>
                </label>
                <div v-if="!salePayments.length" class="text-muted small fst-italic">
                  No customer payments recorded yet.
                </div>
                <div v-for="(p, i) in salePayments" :key="p.id" class="deposit-row">
                  <span class="dep-label">DEP ({{ i + 1 }})</span>
                  <span class="dep-ref">{{ p.referenceNo || '—' }}</span>
                  <span class="dep-amount">({{ formatMoney(p.amount) }})</span>
                  <span class="dep-date">{{ formatShortDate(p.paymentDate) }}</span>
                </div>
              </div>

              <hr class="my-3" />
              <ComputedTotal
                label="TOTAL DEPOSIT = U"
                :value="totalDeposit"
                formula="Σ Customer Payments"
                color="info"
              />
              <ComputedTotal
                label="BALANCE OUTSTANDING = V"
                :value="balanceOutstanding"
                formula="S − Loan − Total Deposit"
                :color="balanceOutstanding > 0 ? 'warning' : 'success'"
              />
            </CCardBody>
          </CCard>

          <!-- Sales Commission -->
          <CCard class="section-card">
            <CCardHeader class="section-header">
              <i class="fas fa-hand-holding-usd me-2"></i>
              <strong>Sales Commission</strong>
            </CCardHeader>
            <CCardBody>
              <EditableNumberField label="SALES COMM (+)" v-model="form.salesCommPlus" />
              <EditableNumberField label="HP (+)" v-model="form.hpCommPlus" />
              <EditableNumberField label="INCENTIVE (+) O/T" v-model="form.incentiveOt" />
              <EditableNumberField label="COMM. PBR" v-model="form.commPbr" />
              <EditableNumberField label="EXTRA" :value="extraComm" :readonly="true" source="Computed: −Balance Outstanding (surplus over sale price)" />
              <hr class="my-2" />
              <EditableNumberField label="BALANCE/DIS (-)" v-model="form.balanceDis" />
              <EditableNumberField label="BROKER/RUNNER (-)" v-model="form.brokerRunner" />
              <EditableNumberField label="STOCK OUTSTATION" v-model="form.stockOutstation" />
              <EditableNumberField label="ADVANCE (-)" v-model="form.advance" />
              <hr class="my-3" />
              <ComputedTotal
                label="TOTAL S/COMM = Z"
                :value="totalSalesComm"
                formula="(Sales + HP + Incentive + PBR + Extra) − (Balance/Dis + Broker + Advance)"
              />
            </CCardBody>
          </CCard>

          <!-- Profit Summary -->
          <CCard class="section-card">
            <CCardHeader class="section-header">
              <i class="fas fa-chart-line me-2"></i>
              <strong>Profit Summary</strong>
            </CCardHeader>
            <CCardBody>
              <EditableNumberField label="CS-CP" v-model="form.csCp" />
              <EditableNumberField label="T. HP (+)" v-model="form.tHp" />
              <EditableNumberField label="TOTAL INCOME. LJ1" v-model="form.totalIncomeLj1" />
              <EditableNumberField label="TOTAL PROFIT" v-model="form.totalProfit" />
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
    </div>

    <!-- ===== PDF generation overlay ===== -->
    <div v-if="generatingPdf" class="pdf-gen-overlay">
      <div class="pdf-gen-card">
        <CSpinner color="primary" />
        <p class="mt-3 mb-0">Generating PDF...</p>
      </div>
    </div>

    <!-- ===== Print Preview (also the source for html2pdf capture) ===== -->
    <!--
      Rendered in normal flow at the bottom of the page so html2canvas can
      reliably snapshot it. Also serves as a WYSIWYG preview of the
      generated PDF — users can see exactly what they will download.
    -->
    <div v-if="loaded" class="pdf-preview-wrap">
      <h5 class="preview-title">
        <i class="fas fa-eye me-2"></i>
        Print Preview
        <small class="text-muted ms-2">— what your PDF will look like</small>
      </h5>

      <div ref="pdfContent" class="lj-pdf-template">
      <h1 class="pdf-title">SALES REPORT(LJ)</h1>

      <table class="pdf-grid">
        <colgroup>
          <col style="width: 21%" />
          <col style="width: 24%" />
          <col style="width: 5%" />
          <col style="width: 5%" />
          <col style="width: 5%" />
          <col style="width: 18%" />
          <col style="width: 22%" />
        </colgroup>
        <tbody>
          <!-- Row 1: S/REP + MONTHS -->
          <tr>
            <td class="label">S/REPRESENTATIVE</td>
            <td colspan="2" class="value highlight">{{ salesmanName }}</td>
            <td colspan="2" class="label">MONTHS</td>
            <td colspan="2" class="value highlight">{{ monthsLabel }}</td>
          </tr>

          <!-- Row 2: blank spacer -->
          <tr class="spacer"><td colspan="7"></td></tr>

          <!-- Row 3: STOCK NO + FINANCE -->
          <tr>
            <td class="label">STOCK NO</td>
            <td colspan="2" class="value highlight">{{ obj.stockNo }}</td>
            <td colspan="2" class="label">FINANCE</td>
            <td colspan="2" class="value highlight">{{ loanBankName }}</td>
          </tr>

          <!-- Row 4: CUSTOMER NAME + REG. DATE -->
          <tr>
            <td class="label">CUSTOMER NAME</td>
            <td colspan="2" class="value highlight">{{ customerName }}</td>
            <td colspan="2" class="label">REG. DATE</td>
            <td colspan="2" class="value">{{ regDateLabel }}</td>
          </tr>

          <!-- Row 5: ADDRESS line 1 + REG. NUMBERS -->
          <tr>
            <td class="label" rowspan="4">ADDRESS</td>
            <td colspan="2" class="value">{{ addressLine(0) }}</td>
            <td colspan="2" class="label">REG. NUMBERS</td>
            <td colspan="2" class="value highlight">{{ regNumber }}</td>
          </tr>
          <!-- Row 6: ADDRESS line 2 + I/C NUMBERS -->
          <tr>
            <td colspan="2" class="value">{{ addressLine(1) }}</td>
            <td colspan="2" class="label">I/C NUMBERS</td>
            <td colspan="2" class="value">{{ customerIc }}</td>
          </tr>
          <!-- Row 7: ADDRESS line 3 + DATE OF BIRTH -->
          <tr>
            <td colspan="2" class="value">{{ addressLine(2) }}</td>
            <td colspan="2" class="label">DATE OF BIRTH</td>
            <td colspan="2" class="value">{{ customerDobDisplay }}</td>
          </tr>
          <!-- Row 8: ADDRESS line 4 + PHONE (H) -->
          <tr>
            <td colspan="2" class="value">{{ addressLine(3) }}</td>
            <td colspan="2" class="label">PHONE NO (H)</td>
            <td colspan="2" class="value">{{ customerPhoneHome }}</td>
          </tr>
          <!-- Row 9: blank-address-cell-already-spanned + PHONE (O) -->
          <tr>
            <td class="value"></td>
            <td colspan="2" class="value">{{ addressLine(4) }}</td>
            <td colspan="2" class="label">PHONE NO (O)</td>
            <td colspan="2" class="value">{{ customerPhoneOffice }}</td>
          </tr>

          <!-- Row 10: spacer -->
          <tr class="spacer"><td colspan="7"></td></tr>

          <!-- Row 11: SALES REPRESENTATIVE | DATE APP APP | PRICE OTR | DATE APP APP -->
          <tr>
            <td class="label">SALES REPRESENTATIVE</td>
            <td class="value highlight">{{ salesmanName }}</td>
            <td class="label-tiny">DATE</td>
            <td class="label-tiny">APP</td>
            <td class="label-tiny">APP</td>
            <td class="label">PRICE (O.T.R)</td>
            <td class="money value">{{ formatMoney(totalCostPrice) }}</td>
          </tr>

          <!-- Row 12: MODEL/COLOUR | INS-10% LESS -->
          <tr>
            <td class="label">MODEL/COLOUR</td>
            <td class="value highlight" colspan="4">{{ modelColor }}</td>
            <td class="label">INS-10% LESS</td>
            <td class="money value">{{ formatMoneyOrBlank(form.ins10PercentLess) }}</td>
          </tr>

          <!-- Row 13: CHASSIS NO | DISCOUNT (first) -->
          <tr>
            <td class="label">CHASSIS NO.</td>
            <td class="value" colspan="4">{{ chassisNo }}</td>
            <td class="label danger">DISCOUNT</td>
            <td class="money value">{{ discountRowMoney(0) }}</td>
          </tr>

          <!-- Row 14: ENGINE NO | DISCOUNT (second) -->
          <tr>
            <td class="label">ENGINE NO.</td>
            <td class="value" colspan="4">{{ engineNo }}</td>
            <td class="label danger">DISCOUNT</td>
            <td class="money value">{{ discountRowMoney(1) }}</td>
          </tr>

          <!-- Row 15: DEP A | WITHDRAW NCD -->
          <tr>
            <td class="label">DEP. A</td>
            <td class="money value" colspan="4">{{ formatMoneyOrBlank(form.depA) }}</td>
            <td class="label">WITHDRAW NCD</td>
            <td class="money value">{{ formatMoneyOrBlank(form.withdrawNcd) }}</td>
          </tr>

          <!-- Row 16: DEP B | TOTAL PRICE OTR -->
          <tr>
            <td class="label">DEP. B</td>
            <td class="money value" colspan="4">{{ formatMoneyOrBlank(form.depB) }}</td>
            <td class="label">TOTAL PRICE (O.T.R)</td>
            <td class="money value bold">{{ formatMoney(totalPriceOtr) }}</td>
          </tr>

          <!-- Row 17: spacer -->
          <tr class="spacer"><td colspan="7"></td></tr>

          <!-- Row 18: BODY PRICE | LOAN -->
          <tr>
            <td class="label">BODY PRICE</td>
            <td class="money value" colspan="4">{{ formatMoney(bodyPrice) }}</td>
            <td class="label">LOAN</td>
            <td class="value highlight money">
              <strong>{{ loanBankName || '—' }}</strong>
              {{ approvedLoanAmount ? `(${formatMoney(approvedLoanAmount)})` : '' }}
            </td>
          </tr>

          <!-- Rows 19-25: deposit breakdown DEP(1)-(7) paired with cost rows -->
          <tr v-for="dep in depositRowPairs" :key="'deprow-' + dep.idx">
            <td class="label" :class="{ highlight: dep.leftHighlight }">{{ dep.leftLabel }}</td>
            <td class="value" :class="{ money: dep.leftIsMoney && !dep.leftNote }" colspan="4">
              <span v-if="dep.leftNote" class="value-split">
                <span class="note-left">{{ dep.leftNote }}</span>
                <span class="amt-right">{{ dep.leftValue }}</span>
              </span>
              <template v-else>{{ dep.leftValue }}</template>
            </td>
            <td class="label">DEP ({{ dep.idx }})</td>
            <td class="money value">
              <span v-if="dep.payment">{{ dep.payment.refNo }} ({{ formatMoney(dep.payment.amount) }}) {{ dep.payment.date }}</span>
            </td>
          </tr>

          <!-- Row 26: INTERCHANGE NO | EXTRA (= Balance Outstanding V) -->
          <tr>
            <td class="label highlight">INTERCHANGE NO</td>
            <td class="value" colspan="4">{{ form.interchangeNo }}</td>
            <td class="label">EXTRA</td>
            <td class="money value">{{ formatMoneyParen(balanceOutstanding) }}</td>
          </tr>

          <!-- Row 27: REGN FEE (COMPANY) | SALES COMMISSION header -->
          <tr>
            <td class="label highlight">REGN FEE (COMPANY)</td>
            <td class="value" colspan="4">{{ miscCostByName('REG FEE (COMPANY)') || miscCostByName('REGN FEE') || '' }}</td>
            <td class="label highlight bold" colspan="2">SALES COMMISSION</td>
          </tr>

          <!-- Row 28: WITHDRAW NCD (label) | SALES COMM (+) -->
          <tr>
            <td class="label">WITHDRAW NCD</td>
            <td class="value" colspan="4"></td>
            <td class="label">SALES COMM (+)</td>
            <td class="money value">{{ formatMoneyOrBlank(form.salesCommPlus) }}</td>
          </tr>

          <!-- Row 29: TENDER NO | HP (+) -->
          <tr>
            <td class="label">TENDER NO</td>
            <td class="value" colspan="4">{{ miscCostByName('TENDER NO') || '' }}</td>
            <td class="label">HP (+)</td>
            <td class="money value">{{ formatMoneyOrBlank(form.hpCommPlus) }}</td>
          </tr>

          <!-- Row 30: ECW TYRE | INCENTIVE -->
          <tr>
            <td class="label">ECW TYRE</td>
            <td class="value" colspan="4">{{ miscCostByName('ECW TYRE') || '' }}</td>
            <td class="label">INCENTIVE (+) O/T</td>
            <td class="money value">{{ formatMoneyOrBlank(form.incentiveOt) }}</td>
          </tr>

          <!-- Row 31: PUSPAKOM | COMM. PBR -->
          <tr>
            <td class="label">PUSPAKOM</td>
            <td class="value" colspan="4">{{ miscCostByName('PUSPAKOM') || '' }}</td>
            <td class="label">COMM. PBR</td>
            <td class="money value">{{ formatMoneyOrBlank(form.commPbr) }}</td>
          </tr>

          <!-- Row 31b: auto-listed custom Miscellaneous Costs (below PUSPAKOM) -->
          <tr v-for="(m, i) in extraMiscCosts" :key="'xmisc-' + i">
            <td class="label">{{ m.name }}</td>
            <td class="money value" colspan="4">{{ formatMoney(m.amount) }}</td>
            <td class="label"></td>
            <td class="value"></td>
          </tr>

          <!-- Row 32: blank | EXTRA -->
          <tr>
            <td class="label"></td>
            <td class="value" colspan="4"></td>
            <td class="label">EXTRA</td>
            <td class="money value">{{ formatMoneyParen(extraComm) }}</td>
          </tr>

          <!-- Row 33: TOTAL COST PRICE | TOTAL Z -->
          <tr>
            <td class="label bold">TOTAL COST PRICE</td>
            <td class="money value bold" colspan="4">{{ formatMoney(totalCostPrice) }}</td>
            <td class="label bold">TOTAL</td>
            <td class="money value bold">{{ formatMoneyParen(totalSalesComm) }}</td>
          </tr>

          <!-- Row 34: spacer -->
          <tr class="spacer"><td colspan="7"></td></tr>

          <!-- Row 35: CS-CP | BALANCE/DIS -->
          <tr>
            <td class="label">CS-CP</td>
            <td class="money value" colspan="4">{{ formatMoneyOrBlank(form.csCp) }}</td>
            <td class="label">BALANCE/DIS (-)</td>
            <td class="money value">{{ formatMoneyOrBlank(form.balanceDis) }}</td>
          </tr>

          <!-- Row 36: T.HP | BROKER/RUNNER -->
          <tr>
            <td class="label">T. HP (+)</td>
            <td class="money value" colspan="4">{{ formatMoneyOrBlank(form.tHp) }}</td>
            <td class="label">BROKER/RUNNER(-)</td>
            <td class="money value">{{ formatMoneyOrBlank(form.brokerRunner) }}</td>
          </tr>

          <!-- Row 37: blank | STOCK OUTSTATION -->
          <tr>
            <td class="label"></td>
            <td class="value" colspan="4"></td>
            <td class="label">STOCK OUTSTATION</td>
            <td class="money value">{{ formatMoneyOrBlank(form.stockOutstation) }}</td>
          </tr>

          <!-- Row 38: TOTAL INCOME LJ1 | ADVANCE -->
          <tr>
            <td class="label">TOTAL INCOME. LJ1</td>
            <td class="money value" colspan="4">{{ formatMoneyOrBlank(form.totalIncomeLj1) }}</td>
            <td class="label">ADVANCE(-)</td>
            <td class="money value">{{ formatMoneyOrBlank(form.advance) }}</td>
          </tr>

          <!-- Row 39: TOTAL PROFIT | TOTAL S/COMM -->
          <tr>
            <td class="label bold">TOTAL PROFIT</td>
            <td class="money value bold" colspan="4">{{ formatMoneyOrBlank(form.totalProfit) }}</td>
            <td class="label bold">TOTAL S/COMM</td>
            <td class="money value bold">{{ formatMoneyParen(totalSalesComm) }}</td>
          </tr>

          <!-- Row 40: spacer -->
          <tr class="spacer"><td colspan="7"></td></tr>

          <!-- Row 41-43: TITLE block on left | SETTLEMENT block on right -->
          <tr>
            <td class="label" rowspan="2">TITLE</td>
            <td class="value">{{ form.title || '' }}</td>
            <td class="label-tiny" colspan="3">DATE(ACCEPT</td>
            <td class="label">SETTLEMENT DATE</td>
            <td class="label highlight">AMT SETTLE :</td>
          </tr>
          <tr>
            <td class="label">NO FS</td>
            <td class="label-tiny" colspan="3">PAYMENT)</td>
            <td class="value">{{ formatShortDateLong(form.settlementDate) }}</td>
            <td class="label highlight">AMT SOLA :</td>
          </tr>
          <tr>
            <td class="label">FLOOR PLAN</td>
            <td class="value">{{ form.floorPlan || '' }}</td>
            <td class="label-tiny" colspan="3"></td>
            <td class="value money">{{ formatMoneyOrBlank(form.amtSettle) }}</td>
            <td class="label highlight">INT FS :</td>
          </tr>
          <tr>
            <td class="label">FLOOR STOCKING</td>
            <td class="value">{{ form.floorStocking || '' }}</td>
            <td class="label-tiny" colspan="3"></td>
            <td class="value money">{{ formatMoneyOrBlank(form.amtSola) }}</td>
            <td class="value money">{{ formatMoneyOrBlank(form.intFs) }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    </div>
  </div>
</template>

<script>
import LjReportApi from "../../lib/ljReportApi";
import html2pdf from "html2pdf.js";

// =================================================================
// Reusable display components defined inline for visual consistency
// across all sections.
// =================================================================

const DerivedField = {
  name: "DerivedField",
  props: {
    label: { type: String, required: true },
    value: { type: [String, Number], default: "" },
    multiline: { type: Boolean, default: false },
    source: { type: String, default: "" },
  },
  template: `
    <div class="field-row derived">
      <div class="field-label">{{ label }}</div>
      <div class="field-value-wrap">
        <div class="field-value" :class="{ 'multiline': multiline }">
          {{ value || '—' }}
        </div>
        <div v-if="source" class="field-source" :title="'Source: ' + source">
          <span class="src-arrow">↳</span> from <em>{{ source }}</em>
        </div>
      </div>
    </div>
  `,
};

const EditableNumberField = {
  name: "EditableNumberField",
  props: {
    label: { type: String, required: true },
    value: { type: [Number, String], default: null },
    readonly: { type: Boolean, default: false },
    source: { type: String, default: "" },
  },
  computed: {
    // Read-only money display: 2 dp, RM prefix, negatives parenthesised —
    // matches the on-screen ComputedTotal convention (e.g. "(RM 2,861.40)").
    readonlyDisplay() {
      const v = Number(this.value) || 0;
      const formatted = Math.abs(v).toLocaleString("en-MY", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      return v < 0 ? `(RM ${formatted})` : `RM ${formatted}`;
    },
  },
  template: `
    <div class="field-row" :class="readonly ? 'derived' : 'editable'">
      <div class="field-label">{{ label }}</div>
      <div class="field-value-wrap">
        <div v-if="readonly" class="field-value">{{ readonlyDisplay }}</div>
        <CInput
          v-else
          type="number"
          step="0.01"
          prepend="RM"
          placeholder="0.00"
          class="editable-input mb-0"
          :value="value"
          @input="$emit('input', $event === '' ? null : Number($event))"
        />
        <div v-if="readonly && source" class="field-source" :title="'Source: ' + source">
          <span class="src-arrow">↳</span> {{ source }}
        </div>
      </div>
    </div>
  `,
};

const EditableTextField = {
  name: "EditableTextField",
  props: {
    label: { type: String, required: true },
    value: { type: String, default: "" },
  },
  template: `
    <div class="field-row editable">
      <div class="field-label">{{ label }}</div>
      <div class="field-value-wrap">
        <CInput
          class="editable-input mb-0"
          :value="value"
          @input="$emit('input', $event)"
        />
      </div>
    </div>
  `,
};

const EditableDateField = {
  name: "EditableDateField",
  props: {
    label: { type: String, required: true },
    value: { type: String, default: null },
  },
  computed: {
    isoValue() {
      return this.value ? String(this.value).slice(0, 10) : "";
    },
  },
  template: `
    <div class="field-row editable">
      <div class="field-label">{{ label }}</div>
      <div class="field-value-wrap">
        <CInput
          type="date"
          class="editable-input mb-0"
          :value="isoValue"
          @input="$emit('input', $event || null)"
        />
      </div>
    </div>
  `,
};

const ComputedTotal = {
  name: "ComputedTotal",
  props: {
    label: { type: String, required: true },
    value: { type: Number, default: 0 },
    formula: { type: String, default: "" },
    color: { type: String, default: "success" },
  },
  computed: {
    formattedValue() {
      const v = Number(this.value) || 0;
      const formatted = Math.abs(v).toLocaleString("en-MY", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      return v < 0 ? `(RM ${formatted})` : `RM ${formatted}`;
    },
  },
  template: `
    <div class="computed-total" :class="'tone-' + color">
      <div class="total-label">{{ label }}</div>
      <div class="total-value">{{ formattedValue }}</div>
      <div v-if="formula" class="total-formula">{{ formula }}</div>
    </div>
  `,
};

export default {
  name: "LjReport",
  components: { DerivedField, EditableNumberField, EditableTextField, EditableDateField, ComputedTotal },
  data() {
    return {
      api: new LjReportApi(),
      loaded: false,
      saving: false,
      generatingPdf: false,
      obj: {},
      form: this.emptyForm(),
    };
  },
  computed: {
    sale() { return this.obj.sale || {}; },
    customer() { return this.sale.customer || {}; },
    vehicle() { return this.obj.vehicle || {}; },
    registration() { return this.obj.registration || {}; },
    adminCost() { return this.obj.adminitrativeCost || {}; },
    loan() { return this.sale.loan || {}; },
    salePayments() {
      return Array.isArray(this.sale.salePayments) ? this.sale.salePayments : [];
    },

    // ----- Derived labels (auto-populated from joined stock data) -----
    salesmanName() {
      return this.sale.salesmanName || (this.sale.salesMan && this.sale.salesMan.fullName) || "";
    },
    customerName() { return this.customer.name || ""; },
    customerAddress() { return this.customer.address || ""; },
    customerIc() { return this.customer.icNumber || ""; },
    customerDobDisplay() {
      const d = this.customer.dateOfBirth;
      if (!d) return "";
      const dt = new Date(d);
      if (isNaN(dt.getTime())) return "";
      return `${String(dt.getDate()).padStart(2, "0")}.${String(dt.getMonth() + 1).padStart(2, "0")}.${String(dt.getFullYear()).slice(2)}`;
    },
    customerPhoneHome() { return this.customer.phoneHome || ""; },
    customerPhoneOffice() { return this.customer.phoneOffice || ""; },

    modelColor() {
      const brand = (this.vehicle.brand && this.vehicle.brand.name) || "";
      const model = (this.vehicle.model && this.vehicle.model.name) || "";
      const color = this.vehicle.color || "";
      return [brand, model, color].filter(Boolean).join(" ");
    },
    chassisNo() { return this.vehicle.chasisNo || ""; },
    engineNo() { return this.vehicle.engineNo || ""; },

    bodyPrice() { return Number(this.adminCost.bodyPrice) || 0; },
    insurance() { return Number(this.adminCost.insuranceAmount) || 0; },
    insuranceCompanyName() { return this.adminCost.insuranceCompanyName || ""; },
    roadtax() { return Number(this.adminCost.roadtaxAmount) || 0; },
    warranty() { return this.sale.warranty || ""; },
    warrantyType() { return this.sale.warrantyType || ""; },
    warrantyAmount() {
      // "Warranty" is a free-text field but is used to hold the Processing
      // Fee amount that is part of the cost price K. Only treat it as money
      // when it parses cleanly as a number (e.g. "3000", "3,000", "RM 3000").
      // Non-numeric notes (e.g. "1 Year") contribute 0.
      const raw = String(this.sale.warranty || "").replace(/rm/gi, "").replace(/[,\s]/g, "");
      const n = Number(raw);
      return isNaN(n) ? 0 : n;
    },

    regDateLabel() {
      const d = this.registration.vehicleRegistrationDate;
      if (!d) return "";
      const dt = new Date(d);
      if (isNaN(dt.getTime())) return "";
      return `${String(dt.getDate()).padStart(2, "0")}.${String(dt.getMonth() + 1).padStart(2, "0")}.${String(dt.getFullYear()).slice(2)}`;
    },
    monthsLabel() {
      const d = this.registration.vehicleRegistrationDate;
      if (!d) return "";
      const dt = new Date(d);
      if (isNaN(dt.getTime())) return "";
      const months = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
      return `${months[dt.getMonth()]} ${dt.getFullYear()}`;
    },
    regNumber() { return this.registration.vehicleRegistrationNumber || ""; },
    loanBankName() { return (this.loan && this.loan.bank && this.loan.bank.name) || ""; },
    approvedLoanAmount() { return Number(this.loan.approvedLoanAmount) || 0; },
    loanLabel() {
      if (!this.approvedLoanAmount && !this.loanBankName) return "";
      const bank = this.loanBankName || "";
      const amt = this.approvedLoanAmount ? ` (RM ${this.formatMoney(this.approvedLoanAmount)})` : "";
      return bank + amt;
    },

    // ----- Computed financial totals (derived from form + derived data) -----
    miscCostsTotal() {
      return (this.form.miscCosts || []).reduce((s, m) => s + (Number(m.amount) || 0), 0);
    },
    // Misc costs that DON'T have a dedicated named row in the printed report.
    // The report hard-codes rows for REGN FEE / TENDER NO / ECW TYRE / PUSPAKOM
    // (matched by name); any other misc cost (e.g. a custom "ABCABC") would
    // otherwise be invisible there, so we auto-list these below PUSPAKOM.
    extraMiscCosts() {
      const reserved = ["reg fee (company)", "regn fee", "tender no", "ecw tyre", "puspakom"];
      return (this.form.miscCosts || []).filter((m) => {
        const n = (m.name || "").trim().toLowerCase();
        return n !== "" && !reserved.includes(n);
      });
    },
    discountsTotal() {
      return (this.form.discounts || []).reduce((s, d) => s + (Number(d.amount) || 0), 0);
    },
    // EXTRA (Sales Commission) is auto-calculated as the surplus collected
    // over the sale price = −(Balance Outstanding V). A negative V (loan +
    // deposits over-cover the price) yields a POSITIVE extra that lifts Z;
    // a positive V (shortfall) yields a negative extra. Not a manual entry —
    // read-only, and feeds Z, the PDF and the saved payload.
    extraComm() { return -this.balanceOutstanding; },
    totalCostPrice() {
      // K = Body + Insurance + Roadtax + TT + Duty + Misc Costs + Processing Fee / Warranty
      const tt = Number(this.form.tt) || 0;
      const duty = Number(this.form.duty) || 0;
      return this.bodyPrice + this.insurance + this.roadtax + tt + duty + this.miscCostsTotal + this.warrantyAmount;
    },
    totalPriceOtr() {
      // S = K - INS10 - Discounts - WithdrawNcd
      const ins10 = Number(this.form.ins10PercentLess) || 0;
      const ncd = Number(this.form.withdrawNcd) || 0;
      return this.totalCostPrice - ins10 - this.discountsTotal - ncd;
    },
    totalDeposit() {
      return this.salePayments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
    },
    balanceOutstanding() {
      // Round to 2 dp to avoid binary floating-point artifacts
      // (e.g. -2861.399999999994) leaking into the EXTRA input and Z.
      const v = this.totalPriceOtr - this.approvedLoanAmount - this.totalDeposit;
      return Math.round((v + Number.EPSILON) * 100) / 100;
    },
    totalSalesComm() {
      const plus =
        (Number(this.form.salesCommPlus) || 0) +
        (Number(this.form.hpCommPlus) || 0) +
        (Number(this.form.incentiveOt) || 0) +
        (Number(this.form.commPbr) || 0) +
        this.extraComm;
      const minus =
        (Number(this.form.balanceDis) || 0) +
        (Number(this.form.brokerRunner) || 0) +
        (Number(this.form.advance) || 0);
      return plus - minus;
    },

    // ----- PDF helpers -----
    depositRowPairs() {
      // Paired rows for the PDF: cost row label on left, DEP(n) on right.
      // The left is a fixed 7-row cost pattern; the right is one DEP row per
      // customer payment. When there are MORE than 7 payments the table
      // auto-grows — extra rows have a blank left side and just carry DEP(n).
      const leftPattern = [
        { leftLabel: "TT", leftValue: this.formatMoneyOrBlank(this.form.tt), leftIsMoney: true },
        { leftLabel: "DUTY", leftValue: this.formatMoneyOrBlank(this.form.duty), leftIsMoney: true },
        { leftLabel: "INSURANCE", leftValue: this.formatMoney(this.insurance), leftNote: this.insuranceCompanyName ? `(${this.insuranceCompanyName})` : "", leftIsMoney: true, leftHighlight: true },
        { leftLabel: "ROAD TAX", leftValue: this.formatMoney(this.roadtax), leftIsMoney: true },
        { leftLabel: "PROCESSING FEE", leftValue: this.warrantyAmount ? this.formatMoney(this.warrantyAmount) : (this.warranty || ""), leftIsMoney: true, leftHighlight: true },
        { leftLabel: "WARRANTY TYPE", leftValue: this.warrantyType, leftIsMoney: false },
        // Blank cost row — kept only to preserve the DEP(7) deposit slot on the right.
        { leftLabel: "", leftValue: "", leftIsMoney: false },
      ];
      const blankRow = { leftLabel: "", leftValue: "", leftIsMoney: false };
      const rowCount = Math.max(leftPattern.length, this.salePayments.length);
      const rows = [];
      for (let i = 0; i < rowCount; i++) {
        const row = leftPattern[i] || blankRow;
        const p = this.salePayments[i] || null;
        rows.push({
          idx: i + 1,
          leftLabel: row.leftLabel,
          leftValue: row.leftValue,
          leftNote: row.leftNote || "",
          leftIsMoney: row.leftIsMoney,
          leftHighlight: !!row.leftHighlight,
          payment: p
            ? {
                refNo: p.referenceNo || "",
                amount: Number(p.amount) || 0,
                date: this.formatShortDate(p.paymentDate),
              }
            : null,
        });
      }
      return rows;
    },
  },
  created() { this.load(); },
  methods: {
    emptyForm() {
      return {
        depA: null, depB: null, tt: null, duty: null,
        interchangeNo: "",
        ins10PercentLess: null, withdrawNcd: null,
        csCp: null, tHp: null, totalIncomeLj1: null, totalProfit: null,
        title: "", noFpFs: "", dateIn: null, floorPlan: "", floorStocking: "",
        settlementDate: null, amtSettle: null, amtSola: null, intFs: null,
        salesCommPlus: null, hpCommPlus: null, incentiveOt: null, commPbr: null, extraComm: null,
        balanceDis: null, brokerRunner: null, stockOutstation: null, advance: null,
        dateApp1: null, dateApp2: null, dateApp3: null,
        app1: "", app2: "", app3: "",
        miscCosts: [],
        discounts: [],
      };
    },
    load() {
      const self = this;
      const stockId = self.$route.params.id;
      self.api
        .get(stockId)
        .then((data) => {
          self.obj = data || {};
          const lj = self.obj.ljReport || {};
          self.form = {
            depA: lj.depA, depB: lj.depB, tt: lj.tt, duty: lj.duty,
            interchangeNo: lj.interchangeNo || "",
            ins10PercentLess: lj.ins10PercentLess, withdrawNcd: lj.withdrawNcd,
            csCp: lj.csCp, tHp: lj.tHp, totalIncomeLj1: lj.totalIncomeLj1, totalProfit: lj.totalProfit,
            title: lj.title || "", noFpFs: lj.noFpFs || "",
            dateIn: lj.dateIn, floorPlan: lj.floorPlan || "", floorStocking: lj.floorStocking || "",
            settlementDate: lj.settlementDate, amtSettle: lj.amtSettle, amtSola: lj.amtSola, intFs: lj.intFs,
            salesCommPlus: lj.salesCommPlus, hpCommPlus: lj.hpCommPlus,
            incentiveOt: lj.incentiveOt, commPbr: lj.commPbr, extraComm: lj.extraComm,
            balanceDis: lj.balanceDis, brokerRunner: lj.brokerRunner,
            stockOutstation: lj.stockOutstation, advance: lj.advance,
            dateApp1: lj.dateApp1, dateApp2: lj.dateApp2, dateApp3: lj.dateApp3,
            app1: lj.app1 || "", app2: lj.app2 || "", app3: lj.app3 || "",
            miscCosts: (lj.miscCosts || []).map((m) => ({
              name: m.name, amount: m.amount, sortOrder: m.sortOrder,
            })),
            discounts: (lj.discounts || []).map((d) => ({
              name: d.name, amount: d.amount, sortOrder: d.sortOrder,
            })),
          };
          self.loaded = true;
        })
        .catch((err) => {
          const msg = (err && err.data && err.data.message) || "Failed to load LJ report";
          self.$root.$emit("show-toast", { header: "Error", message: msg, color: "danger" });
        });
    },
    save() {
      const self = this;
      self.saving = true;
      const stockId = self.$route.params.id;
      const payload = {
        ...self.form,
        // EXTRA is auto-calculated (= Balance Outstanding V); persist the
        // computed value, not the unused form field.
        extraComm: self.extraComm,
        miscCosts: (self.form.miscCosts || []).map((m, i) => ({
          name: m.name || "", amount: Number(m.amount) || 0, sortOrder: i,
        })),
        discounts: (self.form.discounts || []).map((d, i) => ({
          name: d.name || "", amount: Number(d.amount) || 0, sortOrder: i,
        })),
      };
      self.api
        .save(stockId, payload)
        .then(() => {
          self.$root.$emit("show-toast", { header: "Success", message: "LJ Report saved", color: "success" });
        })
        .catch((err) => {
          const msg = (err && err.data && err.data.message) || "Failed to save LJ Report";
          self.$root.$emit("show-toast", { header: "Error", message: msg, color: "danger" });
        })
        .finally(() => {
          self.saving = false;
        });
    },
    async downloadPdf() {
      const self = this;
      self.generatingPdf = true;
      await self.$nextTick();
      await new Promise((r) => setTimeout(r, 250));
      const element = self.$refs.pdfContent;
      const filename = `LJ-${(self.obj.stockNo || "report").replace(/\s+/g, "_")}.pdf`;
      const options = {
        margin: 0,
        filename,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          backgroundColor: "#ffffff",
          // Capture from the element's own position — don't pass windowWidth,
          // it conflicts with `position: fixed` and produces a blank canvas.
          x: 0,
          y: 0,
          scrollX: 0,
          scrollY: -window.scrollY,
        },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      };
      try {
        await html2pdf().from(element).set(options).save();
        self.$root.$emit("show-toast", {
          header: "PDF", message: "LJ Report downloaded", color: "success",
        });
      } catch (e) {
        self.$root.$emit("show-toast", {
          header: "Error", message: "PDF generation failed: " + (e?.message || e),
          color: "danger",
        });
      } finally {
        self.generatingPdf = false;
      }
    },
    goBack() { this.$router.push({ name: "ljReportList" }); },
    addMisc() {
      this.form.miscCosts.push({ name: "", amount: 0, sortOrder: this.form.miscCosts.length });
    },
    removeMisc(i) { this.form.miscCosts.splice(i, 1); },
    addDiscount() {
      this.form.discounts.push({ name: "", amount: 0, sortOrder: this.form.discounts.length });
    },
    removeDiscount(i) { this.form.discounts.splice(i, 1); },

    formatMoney(n) {
      const v = Math.abs(Number(n) || 0);
      return v.toLocaleString("en-MY", {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
      });
    },
    formatMoneyDisplay(n) { return "RM " + this.formatMoney(n); },
    // Accounting-style: negatives are parenthesised, e.g. -2861.4 -> "(2,861.40)".
    // Matches the (RM x) convention used by the on-screen ComputedTotal.
    formatMoneyParen(n) {
      const v = Number(n) || 0;
      return v < 0 ? `(${this.formatMoney(v)})` : this.formatMoney(v);
    },
    formatMoneyOrBlank(n) {
      if (n == null || n === "" || isNaN(n)) return "";
      return this.formatMoney(n);
    },
    formatShortDate(iso) {
      if (!iso) return "";
      const d = new Date(iso);
      if (isNaN(d.getTime())) return "";
      return `${d.getDate()}/${d.getMonth() + 1}`;
    },
    formatShortDateLong(iso) {
      if (!iso) return "";
      const d = new Date(iso);
      if (isNaN(d.getTime())) return "";
      return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getFullYear()).slice(2)}`;
    },
    addressLine(idx) {
      const raw = this.customerAddress || "";
      // Try splitting on newline first; fall back to splitting on commas if
      // the source is one giant line (which it often is in this DB).
      let lines = raw.split(/\r?\n/);
      if (lines.length === 1) {
        lines = raw.split(",").map((s) => s.trim());
      }
      return lines[idx] || "";
    },
    miscCostByName(name) {
      const match = (this.form.miscCosts || []).find(
        (m) => (m.name || "").toUpperCase() === name.toUpperCase()
      );
      if (!match) return "";
      return `${match.name} (${this.formatMoney(match.amount)})`;
    },
    discountRowMoney(idx) {
      const d = (this.form.discounts || [])[idx];
      if (!d) return "";
      return this.formatMoneyOrBlank(d.amount);
    },
  },
};
</script>

<style scoped>
/* ===== Page chrome ===== */
.lj-report-page {
  padding: 1rem;
  background: #f3f4f7;
  min-height: 100vh;
}

.action-bar {
  border-left: 4px solid #321fdb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.action-buttons {
  display: flex;
  align-items: center;
}

/* ===== Card sections ===== */
.section-card {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  margin-bottom: 1rem;
}

.section-header {
  background: #f8f9fa;
  border-bottom: 2px solid #321fdb;
  color: #321fdb;
  padding: 0.5rem 1rem;
  font-size: 0.95rem;
}

/* ===== Field row pattern ===== */
.field-row {
  display: grid;
  grid-template-columns: 42% 58%;
  gap: 0.5rem;
  padding: 0.4rem 0;
  border-bottom: 1px dashed #e4e7ea;
  align-items: start;
}

.field-row:last-child { border-bottom: none; }

.field-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #4f5d73;
  padding-top: 0.4rem;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.field-value-wrap { min-width: 0; }

.field-row.derived .field-value {
  padding: 0.4rem 0.6rem;
  background: #f8f9fa;
  border-radius: 4px;
  font-size: 0.92rem;
  color: #2d3748;
  min-height: 1.4rem;
  word-break: break-word;
}

.field-row.derived .field-value.multiline {
  white-space: pre-line;
}

.field-source {
  font-size: 0.7rem;
  color: #768192;
  font-style: italic;
  margin-top: 2px;
  padding-left: 0.6rem;
}

.field-source em { font-style: normal; }

.src-arrow {
  color: #321fdb;
  font-weight: bold;
  margin-right: 2px;
}

.field-row.editable .field-value-wrap >>> .form-control {
  border-left: 3px solid #321fdb;
}

.editable-input { width: 100%; }

/* ===== Line item rows (misc costs, discounts) ===== */
.line-items {
  background: #fafbfc;
  border: 1px solid #e4e7ea;
  border-radius: 4px;
  padding: 0.5rem 0.75rem;
}

.line-items-label {
  display: block;
  font-size: 0.85rem;
  font-weight: 600;
  color: #4f5d73;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.line-items-label small {
  font-weight: 400;
  text-transform: none;
  letter-spacing: normal;
  margin-left: 0.4rem;
}

.line-item-row {
  display: flex;
  gap: 0.4rem;
  align-items: center;
  margin-bottom: 0.4rem;
}

.amount-input { max-width: 11rem; }

/* ===== Deposit breakdown ===== */
.deposit-breakdown {
  background: #f0f6ff;
  border: 1px solid #cfe0ff;
  border-radius: 4px;
  padding: 0.5rem 0.75rem;
}

.deposit-row {
  display: grid;
  grid-template-columns: 4rem 1fr 7rem 4rem;
  gap: 0.5rem;
  font-size: 0.85rem;
  padding: 0.2rem 0;
  border-bottom: 1px dashed #e4e7ea;
}

.deposit-row:last-child { border-bottom: none; }

.dep-label { font-weight: 700; color: #321fdb; }
.dep-ref { font-family: 'Courier New', monospace; color: #4f5d73; }
.dep-amount { font-family: 'Courier New', monospace; text-align: right; font-weight: 600; }
.dep-date { text-align: right; color: #768192; }

/* ===== Computed total badge ===== */
.computed-total {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 0.5rem;
  padding: 0.7rem 1rem;
  border-radius: 6px;
  border-left: 4px solid #2eb85c;
  background: #e8f7ed;
  margin-top: 0.5rem;
}

.computed-total.tone-info { border-left-color: #39f; background: #e7f1ff; }
.computed-total.tone-warning { border-left-color: #f9b115; background: #fff6e1; }
.computed-total.tone-success { border-left-color: #2eb85c; background: #e8f7ed; }

.total-label {
  font-weight: 700;
  font-size: 0.85rem;
  color: #2d3748;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.total-value {
  font-family: 'Courier New', Consolas, monospace;
  font-weight: 700;
  font-size: 1.1rem;
  color: #1c2742;
  text-align: right;
}

.total-formula {
  grid-column: 1 / -1;
  font-size: 0.7rem;
  font-style: italic;
  color: #768192;
}

/* ===== Print Preview wrap ===== */
/* Visible WYSIWYG preview of the PDF, rendered at the bottom of the
   page after the form cards. Also serves as the html2pdf capture
   target (always in normal flow, so html2canvas snapshots it cleanly). */
.pdf-preview-wrap {
  margin-top: 2rem;
  padding: 1.5rem;
  background: #e9ecef;
  border-radius: 6px;
}

.preview-title {
  font-weight: 600;
  color: #4f5d73;
  margin-bottom: 1rem;
}

/* PDF template — always in normal flow. */
.lj-pdf-template {
  width: 210mm;
  margin: 0 auto;
  padding: 8mm;
  background: white;
  font-family: Arial, Helvetica, sans-serif;
  font-size: 9pt;
  color: #000;
  box-sizing: border-box;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.15);
}

.pdf-gen-overlay {
  position: fixed;
  inset: 0;
  background: rgba(35, 47, 75, 0.7);
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pdf-gen-card {
  background: white;
  border-radius: 8px;
  padding: 2rem 3rem;
  text-align: center;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  min-width: 240px;
}

.pdf-title {
  font-size: 14pt;
  font-weight: bold;
  margin: 0 0 4mm 0;
  letter-spacing: 0.5px;
}

.pdf-grid {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.pdf-grid td {
  border: 1px solid #000;
  padding: 0.6mm 1.2mm;
  vertical-align: middle;
  font-size: 8.5pt;
  line-height: 1.2;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.pdf-grid td.label {
  font-weight: 400;
}

.pdf-grid td.label-tiny {
  font-weight: 400;
  font-size: 7pt;
  text-align: center;
  padding: 0.6mm 0.5mm;
  white-space: nowrap;
}

.pdf-grid td.value { font-weight: 700; }

.pdf-grid td.money {
  text-align: right;
  font-family: 'Courier New', Consolas, monospace;
  font-weight: 400;
}

/* Value cell that carries a left-aligned note (e.g. insurance company name)
   alongside a right-aligned money amount. */
.pdf-grid td.value .value-split {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 6px;
}
.pdf-grid td.value .value-split .note-left {
  text-align: left;
  font-weight: 400;
}
.pdf-grid td.value .value-split .amt-right {
  text-align: right;
  font-family: 'Courier New', Consolas, monospace;
  font-weight: 400;
  white-space: nowrap;
}

.pdf-grid td.bold { font-weight: 700; }

.pdf-grid td.highlight { background: #fff7c0; }

.pdf-grid td.danger { color: #c92626; }

.pdf-grid tr.spacer td {
  border: none;
  padding: 0.5mm 0;
  height: 1mm;
}

@media print {
  .no-print { display: none !important; }
}
</style>
